import React, { useEffect, useState } from 'react';
import { fareAPI, cityAPI, lineAPI, stationAPI } from '../services/api';
import { FareRule, FareRuleType, FareCondition, FareCategory, City, Line, Station } from '../types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
  Autocomplete,
  Stack
} from '@mui/material';
import { 
  Edit, 
  Delete,
  ContentCopy, 
  Refresh, 
  Add, 
  CheckCircle,
  Cancel,
  LocalAtm
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

const FareRules: React.FC = () => {
  const [fareRules, setFareRules] = useState<FareRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<FareRuleType>('CITY');
  const [filterCategory, setFilterCategory] = useState<FareCategory | 'ALL'>('ALL');
  const [sortField, setSortField] = useState<keyof FareRule | ''>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [cities, setCities] = useState<City[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [stations, setStations] = useState<Station[]>([]);
  const [selectedCityId, setSelectedCityId] = useState<string>('');
  const [fareConditions, setFareConditions] = useState<FareCondition[]>([]);
  const [loadingConditions, setLoadingConditions] = useState(false);
  const [showConditionModal, setShowConditionModal] = useState(false);
  const [isEditConditionMode, setIsEditConditionMode] = useState(false);
  const [editingConditionId, setEditingConditionId] = useState<string | null>(null);
  const [conditionForm, setConditionForm] = useState({
    name: '',
    description: '',
    minStations: undefined as number | undefined,
    maxStations: undefined as number | undefined
  });
  const [showBulkCreateModal, setShowBulkCreateModal] = useState(false);
  const [bulkFareCategory, setBulkFareCategory] = useState<FareCategory>('STANDARD');
  const [bulkRules, setBulkRules] = useState([
    { minStations: 0, maxStations: 2, fareAmount: 6 },
    { minStations: 3, maxStations: 5, fareAmount: 12 },
    { minStations: 6, maxStations: 8, fareAmount: 18 },
    { minStations: 9, maxStations: 10, fareAmount: 22 }
  ]);
  const { enqueueSnackbar } = useSnackbar();

  const [form, setForm] = useState<Partial<FareRule>>({
    cityId: '',
    ruleName: '',
    description: '',
    priority: 0,
    fareAmount: 0,
    currency: 'INR',
    isActive: true,
    ruleType: 'CITY',
    fareCategory: 'STANDARD',
    lineId: '',
    stationId: '',
    targetStationId: '',
    stationIds: []
  });

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    if (selectedCityId) {
      fetchFareRules();
      fetchLines();
      fetchStations();
      fetchConditions();
    }
  }, [selectedCityId, activeTab]);

  const fetchCities = async () => {
    try {
      const res = await cityAPI.getAll();
      setCities(res.data);
    } catch (e) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to load cities');
    }
  };

  const fetchFareRules = async () => {
    if (!selectedCityId) return;
    
    setLoading(true);
    setError(null);
    try {
      const res = await fareAPI.getFareRulesByCityAndType(selectedCityId, activeTab);
      setFareRules(res.data);
    } catch (e) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to load fare rules');
      setFareRules([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLines = async () => {
    if (!selectedCityId) return;
    
    try {
      // Try getByCity first, fallback to getAll and filter
      const res = await lineAPI.getByCity(selectedCityId);
      setLines(res.data);
    } catch (e) {
      console.error('Failed to load lines:', e);
      // Fallback: get all lines and filter by cityGroup
      try {
        const allLines = await lineAPI.getAll();
        setLines(allLines.data.filter(line => line.cityGroup === selectedCityId));
      } catch (e2) {
        console.error('Failed to load lines with fallback:', e2);
      }
    }
  };

  const fetchStations = async () => {
    if (!selectedCityId) return;
    
    try {
      const res = await stationAPI.getByCity(selectedCityId);
      setStations(res.data);
    } catch (e) {
      console.error('Failed to load stations:', e);
    }
  };

  const fetchConditions = async () => {
    setLoadingConditions(true);
    try {
      const res = await fareAPI.getActiveConditions();
      setFareConditions(res.data);
    } catch (e) {
      console.error('Failed to load fare conditions:', e);
    } finally {
      setLoadingConditions(false);
    }
  };

  const handleSort = (field: keyof FareRule) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleOpenModal = (rule?: FareRule) => {
    if (rule) {
      setIsEditMode(true);
      setEditingRuleId(rule.id || null);
      setForm({
        ...rule,
        ruleType: rule.ruleType || activeTab,
        fareCategory: rule.fareCategory || 'STANDARD'
      });
    } else {
      setIsEditMode(false);
      setEditingRuleId(null);
      setForm({
        cityId: selectedCityId,
        ruleName: '',
        description: '',
        priority: 0,
        fareAmount: 0,
        currency: 'INR',
        isActive: true,
        ruleType: activeTab,
        fareCategory: 'STANDARD',
        lineId: '',
        stationId: '',
        targetStationId: '',
        stationIds: []
      });
    }
    setShowModal(true);
  };

  const handleCopyRule = (rule: FareRule) => {
    // Copy the rule but treat it as a new rule (not edit mode)
    setIsEditMode(false);
    setEditingRuleId(null);
    setForm({
      ...rule,
      id: undefined, // Remove ID so it's treated as new
      ruleName: `Copy of ${rule.ruleName}`, // Add "Copy of" prefix
      ruleType: rule.ruleType || activeTab,
      fareCategory: rule.fareCategory || 'STANDARD',
      // Keep all other fields as they are
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    setEditingRuleId(null);
  };

  const handleSave = async () => {
    if (!form.cityId || !form.ruleName || form.fareAmount === undefined) {
      enqueueSnackbar('Please fill in all required fields', { variant: 'error' });
      return;
    }

    setSaving(true);
    try {
      if (isEditMode && editingRuleId) {
        await fareAPI.updateFareRule(editingRuleId, form);
        enqueueSnackbar('Fare rule updated successfully', { variant: 'success' });
      } else {
        let response;
        if (form.ruleType === 'CITY') {
          response = await fareAPI.createCityFareRule(form);
        } else if (form.ruleType === 'LINE') {
          if (!form.lineId) {
            enqueueSnackbar('Line ID is required for line fare rules', { variant: 'error' });
            setSaving(false);
            return;
          }
          response = await fareAPI.createLineFareRule(form);
        } else if (form.ruleType === 'STATION') {
          // Validate station selection
          const hasSingleStation = form.stationId && !form.stationIds?.length;
          const hasStationPair = form.stationId && form.targetStationId;
          const hasMultipleStations = form.stationIds && form.stationIds.length > 0;
          
          if (!hasSingleStation && !hasStationPair && !hasMultipleStations) {
            enqueueSnackbar('Please select at least one station for station fare rules', { variant: 'error' });
            setSaving(false);
            return;
          }
          response = await fareAPI.createStationFareRule(form);
        }
        enqueueSnackbar('Fare rule created successfully', { variant: 'success' });
      }
      handleCloseModal();
      fetchFareRules();
    } catch (e) {
      const error = e as ApiError;
      enqueueSnackbar(error.response?.data?.message || error.message || 'Failed to save fare rule', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this fare rule?')) {
      return;
    }

    setDeletingId(id);
    try {
      await fareAPI.deleteFareRule(id);
      enqueueSnackbar('Fare rule deleted successfully', { variant: 'success' });
      fetchFareRules();
    } catch (e) {
      const error = e as ApiError;
      enqueueSnackbar(error.response?.data?.message || error.message || 'Failed to delete fare rule', { variant: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (rule: FareRule) => {
    try {
      if (rule.isActive) {
        await fareAPI.deactivateFareRule(rule.id!);
        enqueueSnackbar('Fare rule deactivated', { variant: 'success' });
      } else {
        await fareAPI.activateFareRule(rule.id!);
        enqueueSnackbar('Fare rule activated', { variant: 'success' });
      }
      fetchFareRules();
    } catch (e) {
      const error = e as ApiError;
      enqueueSnackbar(error.response?.data?.message || error.message || 'Failed to toggle fare rule', { variant: 'error' });
    }
  };

  const getRuleTypeColor = (type: FareRuleType) => {
    switch (type) {
      case 'CITY':
        return 'primary';
      case 'LINE':
        return 'secondary';
      case 'STATION':
        return 'success';
      default:
        return 'default';
    }
  };

  const getRuleScopeLabel = (rule: FareRule) => {
    if (rule.ruleType === 'CITY') {
      return 'All Stations';
    } else if (rule.ruleType === 'LINE') {
      const line = lines.find(l => l.id === rule.lineId);
      return line ? `Line: ${line.name}` : rule.lineId || 'N/A';
    } else if (rule.ruleType === 'STATION') {
      if (rule.stationIds && rule.stationIds.length > 0) {
        // Multiple stations
        const stationNames = rule.stationIds
          .map(id => {
            const station = stations.find(s => s.id === id);
            return station ? station.name : id;
          })
          .filter(Boolean);
        return stationNames.length > 0 
          ? `${stationNames.length} Stations: ${stationNames.join(', ')}`
          : `${rule.stationIds.length} Stations`;
      } else if (rule.targetStationId) {
        // Station pair
        const fromStation = stations.find(s => s.id === rule.stationId);
        const toStation = stations.find(s => s.id === rule.targetStationId);
        return fromStation && toStation 
          ? `${fromStation.name} → ${toStation.name}`
          : `${rule.stationId} → ${rule.targetStationId}`;
      } else if (rule.stationId) {
        // Single station
        const station = stations.find(s => s.id === rule.stationId);
        let label = station ? station.name : rule.stationId || 'N/A';
        if (rule.lineId) {
          const line = lines.find(l => l.id === rule.lineId);
          label += line ? ` (Line: ${line.name})` : ` (Line: ${rule.lineId})`;
        }
        return label;
      }
      return 'N/A';
    }
    return 'N/A';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Fare Rules Management
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchFareRules}
            disabled={loading || !selectedCityId}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal()}
            disabled={!selectedCityId}
          >
            Add Fare Rule
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* City Selection */}
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Select City</InputLabel>
          <Select
            value={selectedCityId}
            onChange={(e) => setSelectedCityId(e.target.value)}
            label="Select City"
          >
            {cities.map((city) => (
              <MenuItem key={city.id} value={city.id}>
                {city.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Rule Type Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="City Rules" value="CITY" />
          <Tab label="Line Rules" value="LINE" />
          <Tab label="Station Rules" value="STATION" />
        </Tabs>
      </Box>

      {/* Category Filter */}
      {selectedCityId && (
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Category</InputLabel>
            <Select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value as FareCategory | 'ALL')}
              label="Filter by Category"
            >
              <MenuItem value="ALL">All Categories</MenuItem>
              <MenuItem value="STANDARD">Standard</MenuItem>
              <MenuItem value="SMART_CARD">Smart Card</MenuItem>
              <MenuItem value="STUDENT">Student</MenuItem>
              <MenuItem value="SENIOR_CITIZEN">Senior Citizen</MenuItem>
              <MenuItem value="CHILD">Child</MenuItem>
              <MenuItem value="DISABLED">Disabled</MenuItem>
              <MenuItem value="WEEKLY_PASS">Weekly Pass</MenuItem>
              <MenuItem value="MONTHLY_PASS">Monthly Pass</MenuItem>
              <MenuItem value="TOURIST">Tourist</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {!selectedCityId ? (
        <Alert severity="info">Please select a city to view fare rules</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : fareRules.length === 0 ? (
        <Alert severity="info">No fare rules found for this city and type</Alert>
      ) : fareRules.filter(rule => filterCategory === 'ALL' || rule.fareCategory === filterCategory || (!rule.fareCategory && filterCategory === 'STANDARD')).length === 0 ? (
        <Alert severity="info">No fare rules found for the selected category</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'ruleName'}
                    direction={sortField === 'ruleName' ? sortDirection : 'asc'}
                    onClick={() => handleSort('ruleName')}
                  >
                    Rule Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'ruleType'}
                    direction={sortField === 'ruleType' ? sortDirection : 'asc'}
                    onClick={() => handleSort('ruleType')}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'fareCategory'}
                    direction={sortField === 'fareCategory' ? sortDirection : 'asc'}
                    onClick={() => handleSort('fareCategory')}
                  >
                    Category
                  </TableSortLabel>
                </TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'priority'}
                    direction={sortField === 'priority' ? sortDirection : 'asc'}
                    onClick={() => handleSort('priority')}
                  >
                    Priority
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'fareAmount'}
                    direction={sortField === 'fareAmount' ? sortDirection : 'asc'}
                    onClick={() => handleSort('fareAmount')}
                  >
                    Fare Amount
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'isActive'}
                    direction={sortField === 'isActive' ? sortDirection : 'asc'}
                    onClick={() => handleSort('isActive')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fareRules
                .filter(rule => filterCategory === 'ALL' || rule.fareCategory === filterCategory || (!rule.fareCategory && filterCategory === 'STANDARD'))
                .sort((a, b) => {
                  if (!sortField) return 0;
                  
                  let aValue: any = a[sortField];
                  let bValue: any = b[sortField];
                  
                  // Handle null/undefined values
                  if (aValue === null || aValue === undefined) aValue = '';
                  if (bValue === null || bValue === undefined) bValue = '';
                  
                  // Handle numeric comparison
                  if (sortField === 'priority' || sortField === 'fareAmount') {
                    const numA = Number(aValue) || 0;
                    const numB = Number(bValue) || 0;
                    return sortDirection === 'asc' ? numA - numB : numB - numA;
                  }
                  
                  // Handle boolean comparison
                  if (sortField === 'isActive') {
                    const boolA = Boolean(aValue);
                    const boolB = Boolean(bValue);
                    if (sortDirection === 'asc') {
                      return boolA === boolB ? 0 : boolA ? 1 : -1;
                    } else {
                      return boolA === boolB ? 0 : boolA ? -1 : 1;
                    }
                  }
                  
                  // Handle string comparison
                  const strA = String(aValue).toLowerCase();
                  const strB = String(bValue).toLowerCase();
                  if (sortDirection === 'asc') {
                    return strA.localeCompare(strB);
                  } else {
                    return strB.localeCompare(strA);
                  }
                })
                .map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>{rule.ruleName}</TableCell>
                  <TableCell>
                    <Chip
                      label={rule.ruleType}
                      color={getRuleTypeColor(rule.ruleType)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={(rule.fareCategory || 'STANDARD').replace(/_/g, ' ')}
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>{getRuleScopeLabel(rule)}</TableCell>
                  <TableCell>{rule.priority}</TableCell>
                  <TableCell>
                    {rule.currency} {rule.fareAmount.toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={rule.isActive ? <CheckCircle /> : <Cancel />}
                      label={rule.isActive ? 'Active' : 'Inactive'}
                      color={rule.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Toggle Active">
                        <IconButton
                          size="small"
                          onClick={() => handleToggleActive(rule)}
                        >
                          {rule.isActive ? <Cancel /> : <CheckCircle />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal(rule)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyRule(rule)}
                          color="primary"
                        >
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(rule.id!)}
                          disabled={deletingId === rule.id}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditMode ? 'Edit Fare Rule' : 'Create Fare Rule'}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh', overflow: 'auto' }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Rule Type</InputLabel>
                <Select
                  value={form.ruleType}
                  onChange={(e) => setForm({ ...form, ruleType: e.target.value as FareRuleType })}
                  label="Rule Type"
                  disabled={isEditMode}
                >
                  <MenuItem value="CITY">City</MenuItem>
                  <MenuItem value="LINE">Line</MenuItem>
                  <MenuItem value="STATION">Station</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Fare Category</InputLabel>
                <Select
                  value={form.fareCategory || 'STANDARD'}
                  onChange={(e) => setForm({ ...form, fareCategory: e.target.value as FareCategory })}
                  label="Fare Category"
                >
                  <MenuItem value="STANDARD">Standard</MenuItem>
                  <MenuItem value="SMART_CARD">Smart Card</MenuItem>
                  <MenuItem value="STUDENT">Student</MenuItem>
                  <MenuItem value="SENIOR_CITIZEN">Senior Citizen</MenuItem>
                  <MenuItem value="CHILD">Child</MenuItem>
                  <MenuItem value="DISABLED">Disabled</MenuItem>
                  <MenuItem value="WEEKLY_PASS">Weekly Pass</MenuItem>
                  <MenuItem value="MONTHLY_PASS">Monthly Pass</MenuItem>
                  <MenuItem value="TOURIST">Tourist</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Rule Name"
                value={form.ruleName}
                onChange={(e) => setForm({ ...form, ruleName: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={form.description || ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>

            {form.ruleType === 'CITY' && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onClick={() => setShowBulkCreateModal(true)}
                  sx={{ mt: 1 }}
                >
                  Create Station Count Based Pricing (Bulk)
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Create multiple city fare rules with station count ranges at once
                </Typography>
              </Grid>
            )}

            {form.ruleType === 'LINE' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Line</InputLabel>
                  <Select
                    value={form.lineId || ''}
                    onChange={(e) => setForm({ ...form, lineId: e.target.value })}
                    label="Line"
                    required
                  >
                    {lines.map((line) => (
                      <MenuItem key={line.id} value={line.id}>
                        {line.name} ({line.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {form.ruleType === 'STATION' && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Station Selection Mode</InputLabel>
                    <Select
                      value={form.stationIds && form.stationIds.length > 0 ? 'MULTIPLE' : 
                             form.targetStationId ? 'PAIR' : 'SINGLE'}
                      onChange={(e) => {
                        const mode = e.target.value;
                        if (mode === 'MULTIPLE') {
                          setForm({ ...form, stationId: '', targetStationId: '', stationIds: [] });
                        } else if (mode === 'PAIR') {
                          setForm({ ...form, stationIds: [], stationId: form.stationId || '' });
                        } else {
                          setForm({ ...form, targetStationId: '', stationIds: [], stationId: form.stationId || '' });
                        }
                      }}
                      label="Station Selection Mode"
                    >
                      <MenuItem value="SINGLE">Single Station</MenuItem>
                      <MenuItem value="PAIR">Station Pair (From → To)</MenuItem>
                      <MenuItem value="MULTIPLE">Multiple Stations</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {(!form.stationIds || form.stationIds.length === 0) && (
                  <Grid item xs={12}>
                    <Autocomplete
                      options={stations}
                      getOptionLabel={(option) => `${option.name} (${option.stationCode})`}
                      value={stations.find(s => s.id === form.stationId) || null}
                      onChange={(_, newValue) => {
                        setForm({ ...form, stationId: newValue?.id || '' });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Station"
                          placeholder="Search and select station..."
                          required
                        />
                      )}
                    />
                  </Grid>
                )}

                {form.stationId && !form.stationIds?.length && (
                  <Grid item xs={12}>
                    <Autocomplete
                      options={stations.filter(s => s.id !== form.stationId)}
                      getOptionLabel={(option) => `${option.name} (${option.stationCode})`}
                      value={stations.find(s => s.id === form.targetStationId) || null}
                      onChange={(_, newValue) => {
                        setForm({ ...form, targetStationId: newValue?.id || undefined });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="To Station (Optional - Leave empty for single station rule)"
                          placeholder="Search and select destination station..."
                        />
                      )}
                    />
                  </Grid>
                )}

                {form.stationIds && form.stationIds.length > 0 ? (
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={stations}
                      getOptionLabel={(option) => `${option.name} (${option.stationCode})`}
                      value={stations.filter(s => form.stationIds?.includes(s.id || ''))}
                      onChange={(_, newValue) => {
                        setForm({ 
                          ...form, 
                          stationIds: newValue.map(s => s.id || '').filter(Boolean),
                          stationId: '',
                          targetStationId: undefined
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Multiple Stations"
                          placeholder="Choose stations..."
                          required
                        />
                      )}
                    />
                  </Grid>
                ) : null}

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Line (Optional - For station rules on specific line)</InputLabel>
                    <Select
                      value={form.lineId || ''}
                      onChange={(e) => setForm({ ...form, lineId: e.target.value || undefined })}
                      label="Line (Optional)"
                    >
                      <MenuItem value="">None (All Lines)</MenuItem>
                      {lines.map((line) => (
                        <MenuItem key={line.id} value={line.id}>
                          {line.name} ({line.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {/* Conditions Section - Available for all rule types */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Conditions (Optional)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    Add conditions to apply this rule only when specific criteria are met (e.g., station count, time, distance)
                  </Typography>
                  <Autocomplete
                    multiple
                    options={fareConditions}
                    getOptionLabel={(option) => option.conditionName || ''}
                    value={fareConditions.filter(c => form.conditionIds?.includes(c.id || ''))}
                    onChange={(_, newValue) => {
                      setForm({ 
                        ...form, 
                        conditionIds: newValue.map(c => c.id || '').filter(Boolean)
                      });
                    }}
                    loading={loadingConditions}
                    ListboxProps={{
                      style: {
                        maxHeight: '400px'
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Conditions"
                        placeholder="Choose conditions..."
                    helperText="Select conditions to apply this rule"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setIsEditConditionMode(false);
                      setEditingConditionId(null);
                      setConditionForm({ name: '', description: '', minStations: undefined, maxStations: undefined });
                      setShowConditionModal(true);
                    }}
                  >
                    + Create New Station Count Condition
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Manage Existing Conditions
                    </Typography>
                    {fareConditions.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No conditions found
                      </Typography>
                    ) : (
                      <TableContainer 
                        component={Paper} 
                        variant="outlined"
                        sx={{ maxHeight: '400px', overflow: 'auto' }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Description</TableCell>
                              <TableCell>Parameters</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {fareConditions.map((condition) => (
                              <TableRow key={condition.id}>
                                <TableCell>{condition.conditionName}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={condition.conditionType.replace(/_/g, ' ')} 
                                    size="small" 
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>{condition.description || '-'}</TableCell>
                                <TableCell>
                                  {condition.conditionType === 'STATION_COUNT' && (
                                    <span>
                                      Min: {condition.parameters?.minStations ?? '-'}, 
                                      Max: {condition.parameters?.maxStations ?? '-'}
                                    </span>
                                  )}
                                  {condition.conditionType === 'DISTANCE' && (
                                    <span>
                                      Min: {condition.parameters?.minDistance ?? '-'}m, 
                                      Max: {condition.parameters?.maxDistance ?? '-'}m
                                    </span>
                                  )}
                                  {condition.conditionType === 'TIME' && (
                                    <span>
                                      Min: {condition.parameters?.minTime ?? '-'}min, 
                                      Max: {condition.parameters?.maxTime ?? '-'}min
                                    </span>
                                  )}
                                  {condition.conditionType === 'WEEKDAY' && (
                                    <span>Type: {condition.parameters?.type || '-'}</span>
                                  )}
                              {condition.conditionType === 'DAY_OF_WEEK' && (
                                <span>
                                  Days: {condition.parameters?.days 
                                    ? (typeof condition.parameters.days === 'string' 
                                        ? (condition.parameters.days as string).split(',').map(d => d.trim().charAt(0) + d.trim().slice(1).toLowerCase()).join(', ')
                                        : (condition.parameters.days as string[]).map(d => d.charAt(0) + d.slice(1).toLowerCase()).join(', '))
                                    : '-'}
                                </span>
                                  )}
                                  {condition.conditionType === 'PEAK_HOURS' && (
                                    <span>
                                      {condition.parameters?.type || '-'} ({condition.parameters?.peakStartTime || '-'} - {condition.parameters?.peakEndTime || '-'})
                                    </span>
                                  )}
                                  {condition.conditionType === 'SEASON' && (
                                    <span>Seasons: {condition.parameters?.seasons || '-'}</span>
                                  )}
                                  {condition.conditionType === 'HOLIDAY' && (
                                    <span>
                                      Dates: {Array.isArray(condition.parameters?.holidayDates) 
                                        ? condition.parameters?.holidayDates.join(', ') 
                                        : condition.parameters?.holidayDates || '-'}
                                    </span>
                                  )}
                                  {!['STATION_COUNT', 'DISTANCE', 'TIME', 'WEEKDAY', 'DAY_OF_WEEK', 'PEAK_HOURS', 'SEASON', 'HOLIDAY'].includes(condition.conditionType) && (
                                    <span>-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={condition.isActive ? 'Active' : 'Inactive'}
                                    size="small"
                                    color={condition.isActive ? 'success' : 'default'}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Tooltip title="Edit">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          // For now, only allow editing STATION_COUNT conditions from this modal
                                          // Other types should be edited from the FareConditions page
                                          if (condition.conditionType === 'STATION_COUNT') {
                                            setIsEditConditionMode(true);
                                            setEditingConditionId(condition.id || null);
                                            setConditionForm({
                                              name: condition.conditionName || '',
                                              description: condition.description || '',
                                              minStations: condition.parameters?.minStations as number | undefined,
                                              maxStations: condition.parameters?.maxStations as number | undefined
                                            });
                                            setShowConditionModal(true);
                                          } else {
                                            enqueueSnackbar('Please edit this condition from the Fare Conditions page', { variant: 'info' });
                                          }
                                        }}
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton
                                        size="small"
                                        onClick={async () => {
                                          if (!condition.id) return;
                                          if (!window.confirm(`Are you sure you want to delete condition "${condition.conditionName}"?`)) {
                                            return;
                                          }
                                          try {
                                            await fareAPI.deleteCondition(condition.id);
                                            await fetchConditions();
                                            enqueueSnackbar('Condition deleted successfully', { variant: 'success' });
                                          } catch (e) {
                                            const error = e as ApiError;
                                            enqueueSnackbar(error.response?.data?.message || error.message || 'Failed to delete condition', { variant: 'error' });
                                          }
                                        }}
                                      >
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Priority"
                type="number"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
                required
                helperText="Higher priority = higher precedence"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Currency"
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fare Amount"
                type="number"
                value={form.fareAmount}
                onChange={(e) => setForm({ ...form, fareAmount: parseFloat(e.target.value) || 0 })}
                required
                inputProps={{ step: '0.01' }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive ?? true}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date (Optional)"
                type="datetime-local"
                value={form.startDate ? form.startDate.substring(0, 16) : ''}
                onChange={(e) => setForm({ ...form, startDate: e.target.value ? `${e.target.value}:00` : undefined })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date (Optional)"
                type="datetime-local"
                value={form.endDate ? form.endDate.substring(0, 16) : ''}
                onChange={(e) => setForm({ ...form, endDate: e.target.value ? `${e.target.value}:00` : undefined })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <LocalAtm />}
          >
            {saving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Station Count Condition Modal */}
      <Dialog open={showConditionModal} onClose={() => {
        setShowConditionModal(false);
        setIsEditConditionMode(false);
        setEditingConditionId(null);
        setConditionForm({ name: '', description: '', minStations: undefined, maxStations: undefined });
      }} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditConditionMode ? 'Edit Station Count Condition' : 'Create Station Count Condition'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Condition Name"
                value={conditionForm.name}
                onChange={(e) => setConditionForm({ ...conditionForm, name: e.target.value })}
                required
                placeholder="e.g., 0-2 Stations"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={conditionForm.description}
                onChange={(e) => setConditionForm({ ...conditionForm, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Min Stations"
                type="number"
                value={conditionForm.minStations ?? ''}
                onChange={(e) => setConditionForm({ 
                  ...conditionForm, 
                  minStations: e.target.value ? parseInt(e.target.value) : undefined 
                })}
                inputProps={{ min: 0 }}
                helperText="Minimum station count (leave empty for no limit)"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Max Stations"
                type="number"
                value={conditionForm.maxStations ?? ''}
                onChange={(e) => setConditionForm({ 
                  ...conditionForm, 
                  maxStations: e.target.value ? parseInt(e.target.value) : undefined 
                })}
                inputProps={{ min: 0 }}
                helperText="Maximum station count (leave empty for no limit)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowConditionModal(false);
            setIsEditConditionMode(false);
            setEditingConditionId(null);
            setConditionForm({ name: '', description: '', minStations: undefined, maxStations: undefined });
          }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!conditionForm.name || (conditionForm.minStations === undefined && conditionForm.maxStations === undefined)) {
                enqueueSnackbar('Please provide a name and at least min or max stations', { variant: 'error' });
                return;
              }
              try {
                if (isEditConditionMode && editingConditionId) {
                  // Update existing condition
                  await fareAPI.updateCondition(editingConditionId, {
                    conditionName: conditionForm.name,
                    description: conditionForm.description,
                    conditionType: 'STATION_COUNT',
                    parameters: {
                      minStations: conditionForm.minStations,
                      maxStations: conditionForm.maxStations
                    }
                  });
                  await fetchConditions();
                  enqueueSnackbar('Condition updated successfully', { variant: 'success' });
                } else {
                  // Create new condition
                  const response = await fareAPI.createStationCountCondition({
                    name: conditionForm.name,
                    description: conditionForm.description,
                    minStations: conditionForm.minStations,
                    maxStations: conditionForm.maxStations
                  });
                  await fetchConditions();
                  setForm({ 
                    ...form, 
                    conditionIds: [...(form.conditionIds || []), response.data.id!]
                  });
                  enqueueSnackbar('Condition created and added', { variant: 'success' });
                }
                setConditionForm({ name: '', description: '', minStations: undefined, maxStations: undefined });
                setShowConditionModal(false);
                setIsEditConditionMode(false);
                setEditingConditionId(null);
              } catch (e) {
                const error = e as ApiError;
                enqueueSnackbar(error.response?.data?.message || error.message || `Failed to ${isEditConditionMode ? 'update' : 'create'} condition`, { variant: 'error' });
              }
            }}
          >
            {isEditConditionMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Create Station Count Based Pricing Modal */}
      <Dialog open={showBulkCreateModal} onClose={() => setShowBulkCreateModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Station Count Based Pricing Rules</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will create multiple city fare rules with station count conditions. Each row represents one fare rule.
          </Alert>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Fare Category</InputLabel>
                <Select
                  value={bulkFareCategory}
                  onChange={(e) => setBulkFareCategory(e.target.value as FareCategory)}
                  label="Fare Category"
                >
                  <MenuItem value="STANDARD">Standard</MenuItem>
                  <MenuItem value="SMART_CARD">Smart Card</MenuItem>
                  <MenuItem value="STUDENT">Student</MenuItem>
                  <MenuItem value="SENIOR_CITIZEN">Senior Citizen</MenuItem>
                  <MenuItem value="CHILD">Child</MenuItem>
                  <MenuItem value="DISABLED">Disabled</MenuItem>
                  <MenuItem value="WEEKLY_PASS">Weekly Pass</MenuItem>
                  <MenuItem value="MONTHLY_PASS">Monthly Pass</MenuItem>
                  <MenuItem value="TOURIST">Tourist</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Min Stations</TableCell>
                  <TableCell>Max Stations</TableCell>
                  <TableCell>Fare Amount (INR)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bulkRules.map((rule, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        type="number"
                        value={rule.minStations}
                        onChange={(e) => {
                          const newRules = [...bulkRules];
                          newRules[index].minStations = parseInt(e.target.value) || 0;
                          setBulkRules(newRules);
                        }}
                        size="small"
                        inputProps={{ min: 0 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={rule.maxStations}
                        onChange={(e) => {
                          const newRules = [...bulkRules];
                          newRules[index].maxStations = parseInt(e.target.value) || 0;
                          setBulkRules(newRules);
                        }}
                        size="small"
                        inputProps={{ min: 0 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={rule.fareAmount}
                        onChange={(e) => {
                          const newRules = [...bulkRules];
                          newRules[index].fareAmount = parseFloat(e.target.value) || 0;
                          setBulkRules(newRules);
                        }}
                        size="small"
                        inputProps={{ step: '0.01', min: 0 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBulkCreateModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!selectedCityId) {
                enqueueSnackbar('Please select a city first', { variant: 'error' });
                return;
              }
              setSaving(true);
              try {
                const createdRules: FareRule[] = [];
                for (const bulkRule of bulkRules) {
                  // Create condition first
                  const conditionResponse = await fareAPI.createStationCountCondition({
                    name: `${bulkRule.minStations}-${bulkRule.maxStations} Stations`,
                    description: `Station count ${bulkRule.minStations} to ${bulkRule.maxStations}`,
                    minStations: bulkRule.minStations,
                    maxStations: bulkRule.maxStations
                  });
                  
                  // Create fare rule with condition
                  const ruleResponse = await fareAPI.createCityFareRule({
                    cityId: selectedCityId,
                    ruleName: `${bulkFareCategory.replace(/_/g, ' ')} Fare for ${bulkRule.minStations}-${bulkRule.maxStations} Stations`,
                    description: `${bulkFareCategory.replace(/_/g, ' ')} fare for ${bulkRule.minStations} to ${bulkRule.maxStations} stations`,
                    fareAmount: bulkRule.fareAmount,
                    currency: 'INR',
                    priority: 0,
                    isActive: true,
                    fareCategory: bulkFareCategory,
                    conditionIds: [conditionResponse.data.id!]
                  });
                  createdRules.push(ruleResponse.data);
                }
                enqueueSnackbar(`Successfully created ${createdRules.length} fare rules`, { variant: 'success' });
                setShowBulkCreateModal(false);
                fetchFareRules();
                fetchConditions();
              } catch (e) {
                const error = e as ApiError;
                enqueueSnackbar(error.response?.data?.message || error.message || 'Failed to create fare rules', { variant: 'error' });
              } finally {
                setSaving(false);
              }
            }}
            disabled={saving}
          >
            {saving ? 'Creating...' : 'Create All Rules'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FareRules;

